import React, { useEffect, useState } from "react";
import { GET } from "@/services/api";
import { useAppSelector } from "@/hooks/hook";
import { API_ROUTES } from "@/services/environment";
import useLoading from "@/hooks/useLoading";
import { Loader } from "@thdinc/hpf-shared";
import EventsTable from "./EventsTable";
import { Booking } from "../Interface";
import SearchInputComponent from "../../eventListPage/SearchInputComponent";
import PaginationRow from "@/components/paginationRow/PaginationRow";

interface CompletedEventsProps {
    openDetailView: (bookingId: string, is_quote_booking?: boolean, isUpcomingEvent?: boolean) => void;
}

const LIMIT = 10;

const CompletedEvents: React.FC<CompletedEventsProps> = ({ openDetailView }) => {
    const { getPastEventApi } = API_ROUTES;
    const { refreshToken } = useAppSelector((store) => store.user);
    const { isLoading, startLoading, stopLoading } = useLoading();

    const [pastEvents, setPastEvents] = useState<Booking[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNext, setHasNext] = useState(false);

    const getPastEvents = async (page: number, search: string) => {
        try {
            startLoading();
            const result = await GET(
                `${getPastEventApi}?pageNumber=${page}&limit=${LIMIT}${search ? `&search=${encodeURIComponent(search)}` : ""
                }`,
                refreshToken,
            );
            if (result?.data?.success) {
                setPastEvents(result.data.result ?? []);
                setTotalResults(result.data.totalResults ?? 0);
                setHasNext(result.data.hasNext ?? false);
                setPageNumber(result.data.pageNumber ?? page);
            }
        } catch (err) {
            console.log(String(err));
        } finally {
            stopLoading();
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 600);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        getPastEvents(1, debouncedSearch);
    }, [debouncedSearch]);

    const handlePrev = () => {
        if (pageNumber > 1) {
            getPastEvents(pageNumber - 1, debouncedSearch);
        }
    };

    const handleNext = () => {
        if (hasNext) {
            getPastEvents(pageNumber + 1, debouncedSearch);
        }
    };

    const currentStart = totalResults === 0 ? 0 : (pageNumber - 1) * LIMIT + 1;
    const currentEnd = Math.min(pageNumber * LIMIT, totalResults);

    return (
        <>
            <div className="mt-1">
                <SearchInputComponent
                    className="flex justify-end items-end"
                    handleSearch={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {isLoading ? (
                <Loader color="bg-red-500" />
            ) : pastEvents.length > 0 ? (
                <>
                    <EventsTable
                        pastEvents={pastEvents}
                        isPast={true}
                        openDetailView={openDetailView}
                    />
                    <PaginationRow
                        onPrev={handlePrev}
                        onNext={handleNext}
                        disablePrev={pageNumber === 1}
                        disableNext={!hasNext}
                        currentStart={currentStart}
                        currentEnd={currentEnd}
                        total={totalResults}
                    />
                </>
            ) : (
                <p className="text-center text-gray-500 text-lg mt-4">No completed events</p>
            )}
        </>
    );
};

export default CompletedEvents;
